import { graphql } from 'services/api';
import * as mutations from './mutations';
import * as queries from './queries';
import { Agent, Tone } from 'generated-graphql/types';

export const createAgent = async ({
  name,
  userId,
  tone,
  organizationId
}: {
  name: string;
  userId: string;
  tone?: Tone;
  organizationId: string;
}): Promise<Agent> => {
  const response = await graphql.mutation(mutations.createAgent, {
    name,
    userId,
    tone: tone || undefined,
    organizationId
  });

  return response.createAgent;
};

export const findAgentForOrg = async ({
  organizationId,
  name,
  tone
}: {
  organizationId: string;
  name: string;
  tone: Tone;
}): Promise<{ id: string } | null> => {
  const filter = { and: [{ name: { eq: name } }, { tone: { eq: tone } }] };
  let nextToken: string | null | undefined;

  // The (name, tone) match is a DynamoDB filter expression, applied only after
  // a page of org agents is read. For an org with many agent rows the match can
  // sit beyond the first page, so we must walk every page before concluding
  // there is no match — otherwise we'd create a duplicate Agent.
  do {
    const response = await graphql.query(queries.getAgentsByOrganization, {
      organizationId,
      filter,
      limit: 1000,
      nextToken
    });

    const connection = response.getAgentsByOrganization;
    const match: Agent | undefined = connection?.items?.find(
      (agent: Agent | null) => agent?.id
    );

    if (match?.id) {
      return { id: match.id };
    }

    nextToken = connection?.nextToken;
  } while (nextToken);

  return null;
};

export const getAgentsByUserAndOrganization = async ({
  userId,
  organizationId
}: {
  userId: string;
  organizationId: string;
}): Promise<{ items: Agent[]; nextToken: string }> => {
  const response = await graphql.query(queries.getAgentsByUserAndOrganization, {
    userId,
    organizationId
  });

  return response.getAgentsByUserAndOrganization;
};
